import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTruck, FiSend, FiInfo, FiAlertCircle, FiCopy, FiList, FiFileText, FiUser,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  recursoTransitoPortalService, TIPO_PEDIDO_RT_LABEL, TIPO_PEDIDO_RT_DESCRICAO,
  type TipoPedidoRT, type AssuntoRT,
} from '../../services/recursoTransito.service';

export function RecursoTransitoAbrirPage() {
  const { cidadao, cadastroCompleto } = useAuth();
  const navigate = useNavigate();

  const [tipoPedido, setTipoPedido] = useState<TipoPedidoRT>('recurso_multa');
  const [assuntoId, setAssuntoId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');

  const [numeroAit, setNumeroAit] = useState('');
  const [placaVeiculo, setPlacaVeiculo] = useState('');
  const [dataInfracao, setDataInfracao] = useState('');
  const [localInfracao, setLocalInfracao] = useState('');
  const [codigoInfracao, setCodigoInfracao] = useState('');
  const [valorMulta, setValorMulta] = useState('');
  const [condutorNome, setCondutorNome] = useState('');
  const [condutorCpf, setCondutorCpf] = useState('');

  const [assuntos, setAssuntos] = useState<AssuntoRT[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ numero: string; hash_publico: string } | null>(null);

  useEffect(() => {
    recursoTransitoPortalService.listarAssuntos(getEstabelecimentoAtualId())
      .then((a) => {
        setAssuntos(a);
        if (a.length > 0) setAssuntoId(a[0].id);
      })
      .finally(() => setCarregando(false));
  }, []);

  const exigeIndicacaoCondutor = tipoPedido === 'indicacao_condutor';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cidadao) return;
    if (titulo.trim().length < 3) { setErro('Informe um título.'); return; }
    if (descricao.trim().length < 20) { setErro('Detalhe seus argumentos (mínimo 20 caracteres).'); return; }
    if (!assuntoId) { setErro('Selecione um assunto.'); return; }
    if (!numeroAit.trim()) { setErro('Informe o número do Auto de Infração (AIT).'); return; }
    if (!placaVeiculo.trim()) { setErro('Informe a placa do veículo.'); return; }
    if (exigeIndicacaoCondutor && (!condutorNome.trim() || !condutorCpf.trim())) {
      setErro('Para indicar condutor, informe nome e CPF do real condutor.');
      return;
    }

    setErro(null);
    setEnviando(true);
    try {
      const r = await recursoTransitoPortalService.abrir({
        estabelecimento_id: getEstabelecimentoAtualId(),
        cidadao_id: cidadao.id,
        assunto_id: assuntoId,
        tipo_pedido: tipoPedido,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        numero_ait: numeroAit.trim(),
        placa_veiculo: placaVeiculo.trim().toUpperCase(),
        data_infracao: dataInfracao || undefined,
        local_infracao: localInfracao.trim() || undefined,
        codigo_infracao: codigoInfracao.trim() || undefined,
        valor_multa: valorMulta ? Number(valorMulta) : undefined,
        condutor_nome: condutorNome.trim() || undefined,
        condutor_cpf: condutorCpf.trim() || undefined,
      });
      setResultado({ numero: r.numero, hash_publico: r.hash_publico });
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  }

  if (resultado) {
    return <PainelSucesso numero={resultado.numero} hash={resultado.hash_publico} onVer={() => navigate(`/solicitacao/${resultado.numero}`)} />;
  }

  if (!cadastroCompleto) {
    return (
      <PortalPage
        titulo="Recurso de trânsito"
        subtitulo="Defesa prévia, recurso de multa (JARI) e indicação de condutor."
        icone={<FiTruck size={26} />}
        voltarHref="/"
      >
        <SectionCard titulo="Complete seu cadastro" icone={<FiUser size={18} />}>
          <Alert variante="aviso">
            Para protocolar um recurso de trânsito você precisa completar seu cadastro
            (CPF, telefone e endereço).
          </Alert>
          <div className="flex justify-end mt-4">
            <Button onClick={() => navigate('/completar-cadastro')}>Completar cadastro</Button>
          </div>
        </SectionCard>
      </PortalPage>
    );
  }

  return (
    <PortalPage
      titulo="Recurso de trânsito"
      subtitulo="Defesa prévia, recurso de multa (JARI) e indicação de condutor — Código de Trânsito Brasileiro (Lei 9.503/97)."
      icone={<FiTruck size={26} />}
      voltarHref="/"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <Alert variante="info">
          <strong>Atenção aos prazos.</strong> Defesa prévia: até 30 dias da notificação de autuação.
          Recurso de multa (JARI): até 30 dias da notificação de penalidade. Indicação de condutor:
          até 30 dias para evitar a multa por não-indicação.
        </Alert>

        <SectionCard titulo="Tipo de pedido e assunto" icone={<FiList size={18} />}>
          <div className="space-y-4">
            <div>
              <Label obrigatorio>Tipo de pedido</Label>
              <div className="mt-2 space-y-2">
                {(Object.keys(TIPO_PEDIDO_RT_LABEL) as TipoPedidoRT[]).map((t) => (
                  <label key={t} className={`block p-3 rounded-md border cursor-pointer transition ${
                    tipoPedido === t ? 'border-institutional bg-institutional/5' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="tipo-pedido"
                        checked={tipoPedido === t}
                        onChange={() => setTipoPedido(t)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{TIPO_PEDIDO_RT_LABEL[t]}</div>
                        <p className="text-xs text-slate-600 mt-0.5">{TIPO_PEDIDO_RT_DESCRICAO[t]}</p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label obrigatorio>Assunto</Label>
              {carregando ? (
                <div className="h-10 flex items-center"><Spinner tamanho="sm" /></div>
              ) : assuntos.length === 0 ? (
                <p className="text-xs text-amber-700 mt-1">
                  Nenhum assunto de Recurso de Trânsito configurado pela prefeitura.
                </p>
              ) : (
                <select
                  value={assuntoId}
                  onChange={(e) => setAssuntoId(e.target.value)}
                  required
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
                >
                  {assuntos.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </SectionCard>

        <SectionCard titulo="Dados da infração" icone={<FiTruck size={18} />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <Label htmlFor="ait" obrigatorio>Auto de Infração (AIT)</Label>
              <Input id="ait" value={numeroAit} onChange={(e) => setNumeroAit(e.target.value)} required placeholder="ex: AB12345678" />
            </div>
            <div>
              <Label htmlFor="placa" obrigatorio>Placa</Label>
              <Input
                id="placa"
                value={placaVeiculo}
                onChange={(e) => setPlacaVeiculo(e.target.value.toUpperCase())}
                required
                placeholder="ABC1D23"
                maxLength={8}
                className="font-mono uppercase"
              />
            </div>
            <div>
              <Label htmlFor="data-inf">Data da infração</Label>
              <Input id="data-inf" type="date" value={dataInfracao} onChange={(e) => setDataInfracao(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cod-inf">Código CTB</Label>
              <Input id="cod-inf" value={codigoInfracao} onChange={(e) => setCodigoInfracao(e.target.value)} placeholder="ex: 218-II" />
            </div>
            <div>
              <Label htmlFor="valor">Valor da multa (R$)</Label>
              <Input id="valor" type="number" step="0.01" min="0" value={valorMulta} onChange={(e) => setValorMulta(e.target.value)} placeholder="195,23" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="local">Local da infração</Label>
              <Input id="local" value={localInfracao} onChange={(e) => setLocalInfracao(e.target.value)} placeholder="ex: Av. Brasil, em frente ao nº 1500" />
            </div>
          </div>
        </SectionCard>

        {exigeIndicacaoCondutor && (
          <SectionCard titulo="Real condutor" icone={<FiUser size={18} />}>
            <p className="text-xs text-slate-600 mb-4">
              Identifique a pessoa que estava conduzindo o veículo no momento da infração.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
              <div>
                <Label htmlFor="cond-nome" obrigatorio>Nome completo</Label>
                <Input id="cond-nome" value={condutorNome} onChange={(e) => setCondutorNome(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="cond-cpf" obrigatorio>CPF</Label>
                <Input id="cond-cpf" value={condutorCpf} onChange={(e) => setCondutorCpf(e.target.value)} required placeholder="000.000.000-00" />
              </div>
            </div>
          </SectionCard>
        )}

        <SectionCard titulo="Pedido e argumentação" icone={<FiFileText size={18} />}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="titulo" obrigatorio>Título</Label>
              <Input id="titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} required placeholder="Resuma seu pedido em uma frase" />
            </div>
            <div>
              <Label htmlFor="desc" obrigatorio>Argumentação</Label>
              <textarea
                id="desc"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={6}
                required
                placeholder="Descreva os fundamentos do seu pedido (mínimo 20 caracteres). Cite artigos do CTB se aplicável, anexe provas (fotos, documentos) na próxima etapa..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
              />
              <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                <FiInfo size={12} className="flex-shrink-0 mt-0.5" />
                <span>Após protocolar, você poderá anexar fotos da multa, CNH, documentos do veículo e outras provas.</span>
              </p>
            </div>
          </div>
        </SectionCard>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/')}>Cancelar</Button>
          <Button type="submit" loading={enviando} disabled={assuntos.length === 0}>
            <FiSend size={14} /> Protocolar pedido
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}

function PainelSucesso({ numero, hash, onVer }: { numero: string; hash: string; onVer: () => void }) {
  const link = `${window.location.origin}/protocolo/${numero}/${hash}`;
  const [copiado, setCopiado] = useState(false);
  function copiar() {
    navigator.clipboard.writeText(link);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <PainelResultado
      titulo="Pedido protocolado"
      descricao={<>Seu protocolo é <strong className="text-slate-900">{numero}</strong>. A prefeitura analisará e responderá conforme o prazo legal.</>}
      acoes={<Button onClick={onVer}>Acompanhar pedido</Button>}
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Label>Link público de acompanhamento</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input value={link} readOnly className="text-xs flex-1" />
          <Button type="button" variant="outline" onClick={copiar}>
            <FiCopy size={14} /> {copiado ? 'Copiado!' : 'Copiar'}
          </Button>
        </div>
      </div>

      <Alert variante="info" className="mt-4">
        <FiAlertCircle size={12} className="inline mr-1" />
        Você receberá notificações por e-mail a cada movimentação. Em caso de indeferimento de
        recurso de multa, você poderá recorrer ao CETRAN (2ª instância).
      </Alert>
    </PainelResultado>
  );
}
